import {
  ComponentType,
  createContext,
  ReactNode, useCallback, useContext, useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

export type ModalReactElementProps<T, R> = T & {
  close: (data?: R) => void;
};

type OpenModalProps<T, R> = {
  Component: ComponentType<ModalReactElementProps<T, R>>;
  props: T;
}

type AnyModalState = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: ComponentType<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  resolve: (data?: any) => void;
};

type ModalContextProps = {
  openModal: <T, R>(props: OpenModalProps<T, R>) => Promise<R>;
}

const ModalContext = createContext<ModalContextProps | undefined>(undefined);

export const ModalProvider = (({ children }: { children: ReactNode }) => {
  const [modal, setModal] = useState<AnyModalState | undefined>(undefined);

  const openModal = useCallback(
    <T, R>(
      { Component, props }: OpenModalProps<T, R>,
    ) => new Promise<R>((resolve) => {
      setModal({
        Component,
        props,
        resolve,
      });
    }),
    [],
  );

  const providerValue = useMemo(() => ({ openModal }), [openModal]);
  const onCloseModal = useCallback((currentModal: AnyModalState, data?: unknown) => {
    currentModal.resolve(data);
    setModal(undefined);
  }, []);
  const modalComponent = modal
    && createPortal(
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <div
        className="modal-overlay"
        onClick={() => onCloseModal(modal)}
      >
        <modal.Component
          {...modal.props}
          close={(data: unknown) => onCloseModal(modal, data)}
        />
      </div>,
      document.body,
    );

  return (
    <ModalContext.Provider value={providerValue}>
      {children}
      {modalComponent}
    </ModalContext.Provider>
  );
});

export const useModal = () => {
  const context = useContext(ModalContext);

  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }

  return context;
};
